import React, { useState, useEffect } from "react";
import { FiMenu, FiX, FiMail } from "react-icons/fi";
import logo from "../asset/images/logobft.png";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { scroller } from "react-scroll";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  /* ── scroll detection for navbar styling ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── set active tab from current route ── */
  useEffect(() => {
    const p = location.pathname;
    if (p === "/") setActiveTab("home");
    else if (p.startsWith("/AboutUs")) setActiveTab("about");
    else if (p.startsWith("/services")) setActiveTab("services");
    else if (p.startsWith("/case-study")) setActiveTab("portfolio");
    else setActiveTab("");
  }, [location.pathname]);

  /* ── close all mobile panels ── */
  const closeAll = () => {
    setMobileMenuOpen(false);
    setMobileAboutOpen(false);
    setMobileServicesOpen(false);
  };

  /* ── navigation helpers ── */
  const scrollToSection = (sectionId, tabName) => {
  closeAll();
  setActiveTab(tabName);
  if (location.pathname === "/") {
    scroller.scrollTo(sectionId, { duration: 800, smooth: "easeInOutQuart", offset: -80 });
  } else {
    navigate("/", { state: { scrollTo: sectionId } }); // ✅ removed replace: true
  }
};

  const goHome = () => {
    closeAll();
    setActiveTab("home");
    if (location.pathname === "/") {
      scroller.scrollTo("home", { duration: 800, smooth: "easeInOutQuart", offset: -80 });
    } else {
      navigate("/", { replace: true });
    }
  };

  /* ── static nav data ── */
  const AboutUs = [
    { label: "Our Company", path: "/AboutUs/Company" },
    { label: "Meet Our Team", path: "/AboutUs/TeamSection" },
  ];
  const services = [
    { label: "Generative AI", path: "/services/gen-ai" },
    { label: "Mobile App Development", path: "/services/mobile-app-development" },
    { label: "Web Development", path: "/services/web-development" },
    { label: "DevOps Consulting", path: "/services/devops-consulting" },
    { label: "Salesforce Development", path: "/services/Salesforce-services" },
  ];
  const emails = [
    { label: "Connect with us", address: "connect@bytesfarms.co.in" },
    { label: "Career", address: "career@bytesfarms.co.in" },
    { label: "HR", address: "hr@bytesfarms.co.in" },
  ];

  /* ── FIXED CSS STRINGS ── */
  // Added -mt-[1px] and ensured it sits correctly relative to the parent
  const DROP_WRAP =
    "absolute top-full left-0 z-50 hidden group-hover:flex flex-col w-[260px] bg-[#ff9933] shadow-lg text-white font-semibold pt-0";
  const DROP_ITEM =
    "px-4 py-3 hover:bg-[#1b2f5a] border-b border-[#e68300] last:border-b-0 transition-colors duration-150";

  /* ── reusable nav button ── */
  const NavBtn = ({ tab, onClick, children, extra = "" }) => (
    <button
      onClick={onClick}
      className={`bg-transparent border-none font-semibold transition-colors duration-150 py-2
        ${activeTab === tab
          ? "text-[#f7941d] cursor-default"
          : "text-[#1b2f5a] hover:text-[#f7941d] cursor-pointer"}
        ${extra}`}
    >
      {children}
    </button>
  );

  /* ════════════════════════════════════════════════════════════════
     DESKTOP NAV
  ════════════════════════════════════════════════════════════════ */
  const DesktopNav = () => (
    <nav className="hidden md:flex items-center space-x-10 font-semibold tracking-wide">
      <NavBtn tab="home" onClick={goHome}>HOME</NavBtn>

      {/* Added h-full and flex items-center to parent to ensure the dropdown triggers from the very bottom of the nav bar */}
      <div className="relative group flex items-center h-full py-4">
        <NavBtn tab="about" onClick={() => scrollToSection("about", "about")} extra="flex items-center gap-1">
          ABOUT <span className="text-[#f7941d]">&#9662;</span>
        </NavBtn>
        <div className={DROP_WRAP}>
          {AboutUs.map(({ label, path }, i) => (
            <RouterLink key={i} to={path} className={DROP_ITEM}>{label}</RouterLink>
          ))}
        </div>
      </div>

      <div className="relative group flex items-center h-full py-4">
        <NavBtn tab="services" onClick={() => scrollToSection("services", "services")} extra="flex items-center gap-1">
          SERVICES <span className="text-[#f7941d]">&#9662;</span>
        </NavBtn>
        <div className={DROP_WRAP}>
          {services.map(({ label, path }, i) => (
            <RouterLink key={i} to={path} className={`block ${DROP_ITEM}`}>{label}</RouterLink>
          ))}
        </div>
      </div>

      <NavBtn tab="portfolio" onClick={() => scrollToSection("portfolio", "portfolio")}>PORTFOLIO</NavBtn>

      <NavBtn tab="contact" onClick={() => scrollToSection("contact", "contact")}>
        CONTACT US
      </NavBtn>
    </nav>
  );

  return (
    <header className="w-full fixed top-0 left-0 z-50 bg-white">
      <div className={`transition-all duration-300 ${scrolled ? "py-2 shadow-md border-b border-gray-200" : "py-0"}`}>
        <div className="max-w-7xl mx-auto px-4">
          {scrolled ? (
            <div className="flex items-center justify-between">
              <img onClick={goHome} src={logo} alt="Logo" className="h-12 w-52 object-contain cursor-pointer" />
              <div className="flex items-center space-x-6">
                <DesktopNav />
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-2xl text-[#1b2f5a] p-2 hover:bg-gray-100 rounded">
                  {mobileMenuOpen ? <FiX /> : <FiMenu />}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col md:flex-row items-center justify-between py-3 gap-3">
                <img onClick={goHome} src={logo} alt="Logo" className="h-20 w-80 object-contain cursor-pointer" />
                <div className="hidden md:flex items-center gap-6">
      {/* ── SOPHISTICATED HORIZONTAL INFO-BAR ── */}
<div className="hidden lg:flex items-center bg-[#f8fafc] px-6 py-2.5 rounded-full border border-gray-100 shadow-sm">
  {emails.map(({ label, address }, i) => (
    <React.Fragment key={i}>
      <a 
        href={`mailto:${address}`} 
        className="group flex items-center gap-2.5 transition-all duration-300"
      >
        {/* Simple Minimal Icon */}
        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-white border border-gray-200 group-hover:bg-[#1b2f5a] group-hover:border-[#1b2f5a] transition-all duration-300 shadow-sm">
          <FiMail className="text-[#f7941d] group-hover:text-white text-[12px]" />
        </div>

        {/* Clean Typography */}
        <div className="flex flex-col leading-none">
          <span className="text-[9px] font-extrabold text-[#1b2f5a]/50 uppercase tracking-widest mb-0.5">
            {label}
          </span>
          <span className="text-[12px] font-bold text-[#1b2f5a] group-hover:text-[#f7941d] transition-colors">
            {address}
          </span>
        </div>
      </a>

      {/* Vertical Divider (Except after the last item) */}
      {i < emails.length - 1 && (
        <div className="h-6 w-[1.5px] bg-gray-200 mx-5 rotate-[15deg]"></div>
      )}
    </React.Fragment>
  ))}
</div>
                </div>
              </div>
              <hr className="border-t border-gray-300 mb-3" />
              <div className="flex flex-col md:flex-row items-center justify-between">
                <DesktopNav />
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-2xl text-[#1b2f5a] p-2 hover:bg-gray-100 rounded">
                  {mobileMenuOpen ? <FiX /> : <FiMenu />}
                </button>
              </div>
            </>
          )}
        </div>

        {/* MOBILE MENU */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
            <nav className="flex flex-col p-4 space-y-4">
              <NavBtn tab="home" onClick={goHome}>HOME</NavBtn>
              <div className="border-b border-gray-200 pb-2">
                <button onClick={() => setMobileAboutOpen(!mobileAboutOpen)} className={`flex items-center gap-1 font-semibold ${activeTab === "about" ? "text-[#f7941d]" : "text-[#1b2f5a]"}`}>
                  ABOUT <span className="text-[#f7941d]">{mobileAboutOpen ? "▲" : "▼"}</span>
                </button>
                {mobileAboutOpen && (
                  <div className="ml-4 mt-2 space-y-2">
                    {AboutUs.map(({ label, path }, i) => (
                      <RouterLink key={i} to={path} onClick={closeAll} className="block text-sm text-gray-600 hover:text-[#f7941d] py-1">{label}</RouterLink>
                    ))}
                  </div>
                )}
              </div>
              <div className="border-b border-gray-200 pb-2">
                <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className={`flex items-center gap-1 font-semibold ${activeTab === "services" ? "text-[#f7941d]" : "text-[#1b2f5a]"}`}>
                  SERVICES <span className="text-[#f7941d]">{mobileServicesOpen ? "▲" : "▼"}</span>
                </button>
                {mobileServicesOpen && (
                  <div className="ml-4 mt-2 space-y-2">
                    {services.map(({ label, path }, i) => (
                      <RouterLink key={i} to={path} onClick={closeAll} className="block text-sm text-gray-600 hover:text-[#f7941d] py-1">{label}</RouterLink>
                    ))}
                  </div>
                )}
              </div>
              <div className="border-b border-gray-200 pb-2">
                <NavBtn tab="portfolio" onClick={() => scrollToSection("portfolio", "portfolio")}>PORTFOLIO</NavBtn>
              </div>
              <div className="border-b border-gray-200 pb-2">
                <NavBtn tab="contact" onClick={() => scrollToSection("contact", "contact")}>CONTACT US</NavBtn>
              </div>
              <div className="pt-2 space-y-2">
                {emails.map(({ label, address }, i) => (
                  <a key={i} href={`mailto:${address}`} className="flex items-center gap-3 py-1">
                    <FiMail className="text-[#f7941d]" />
                    <div className="flex flex-col">
                      <span className="text-[10px] text-gray-400 uppercase">{label}</span>
                      <span className="text-xs font-semibold text-[#1b2f5a]">{address}</span>
                    </div>
                  </a>
                ))}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;